'use client'

import { ApolloProvider } from '@apollo/client'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import type { ReactNode } from 'react'
import React, { lazy, Suspense, useMemo } from 'react'

import { ChatProvider } from '../contexts/chat-context'
import { useRemovePointerEvents } from '../hooks/use-remove-pointer-events'
import { client } from '../lib/apollo-client'
import { ChainLoader } from './chain-loader'
import { SentryInit } from './sentry-client'
import { ChainThemeProvider } from './theme-provider'

const AnalyticsProvider = lazy(() =>
  import('../contexts/analytics-context').then((module) => ({ default: module.AnalyticsProvider }))
)

const AnalyticsProviderWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<ChainLoader />}>
    <AnalyticsProvider>{children}</AnalyticsProvider>
  </Suspense>
)

export const Providers: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Memoize Apollo client to prevent unnecessary re-creations
  const apolloClient = useMemo(() => client, [])
  useRemovePointerEvents()

  return (
    <ChainThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem={true}
      disableTransitionOnChange={true}
      storageKey="chaine-theme"
    >
      <ApolloProvider client={apolloClient}>
        <NuqsAdapter>
          <AnalyticsProviderWrapper>
            <ChatProvider>
              <SentryInit />
              {children}
            </ChatProvider>
          </AnalyticsProviderWrapper>
        </NuqsAdapter>
      </ApolloProvider>
    </ChainThemeProvider>
  )
}
